import { EditArt } from "src/app/_interfaces/EditArt";
import { FeedbackService } from "src/app/_provider/feedback.service";
import { PopoverPage } from "../../popover/popover.page";
import { HttpService } from "src/app/_provider/http.service";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import * as Quill  from 'quill';
import { HttpFunctionResult } from "src/app/_interfaces/HttpFunctionResult";
import { SocketMessageData, SocketsService } from "src/app/_provider/sockets.service";
import * as signalR from "@microsoft/signalr"
import { element } from "protractor";

export abstract class EditorFormat{
    constructor(
        public feedback: FeedbackService,
        public http: HttpService,
        public socketSrv: SocketsService
    ) {}

    
    private _selectedFormat : any = {};
    public get selectedFormat() : any {
        return this._selectedFormat;
    }
    public set selectedFormat(v : any) {
        this._selectedFormat = v;
    }
    
    private _formatEditor : Quill;
    public get formatEditor() : Quill {
        return this._formatEditor;
    }
    public set formatEditor(v : Quill) {
        this._formatEditor = v;
        this.formatEditor.on("editor-change", (eventName, ...args) =>{
            if((!args[0] || !args[1] || !args[2])) return
            this.selectedFormat = this.formatEditor?.getFormat()
            // console.log(this.selectedFormat);
        })
    }
        
    
    /**
     * Ändert die Schriftgröße des Texteditors.
     * @param size Die Größe der Schrift in Pixeln. Wenn 0 übergeben wird, wird die Schriftgröße entfernt.
     */
    public changeFontSize(size: number){
        if(size === 0) this._formatEditor.format("size", "")
        else this._formatEditor.format("size", `${size}px`)
    }

    /**
     * Ändert die Schriftart des Texteditors.
     * @param font Die Schriftart, die ausgewählt wurde.
     */
    public changeFontColor(color: "white" | "black" | "red" | "green" | "blue" | "yellow"){
        const colorCode: string = this.getColorCode(color)
        this._formatEditor.format("color", colorCode)
        this.selectedFormat.color = colorCode
    }
    
    /**
     * Ändert die Hintergrundfarbe des Texteditors.
     * @param color Die Hintergrundfarbe, die ausgewählt wurde.
     */
    public changeBackgroundColor(color: "none" | "white" | "black" | "red" | "green" | "blue" | "yellow"){
        if(color != "none") {
            const colorCode: string =  this.getColorCode(color)
            this._formatEditor.format("background", colorCode)
            this.selectedFormat.background = colorCode
        } 
        else this._formatEditor.format("background", "")
        
    }

    /**
     * Ändert die Schriftart des Texteditors.
     * @param font Die Schriftart, die ausgewählt wurde.
     */
    private getColorCode(color: "none" | "white" | "black" | "red" | "green" | "blue" | "yellow"): string{
        switch (color) {
            case "white": return ""
            case "black": return "#000000"
            case "red": return "#ff6666"
            case "green": return "#8caf6e"
            case "blue": return "#6576ad"
            default: return "#C9B235"
        }
    }


    /**
     * Ändert die Schriftart des Texteditors.
     * @param font Die Schriftart, die ausgewählt wurde.
     */
    public getSelectedBackground(hexColor: string){
        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
        return result ? (`rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, 0.4)`) : ""
    }

    /**
     * Ändert die Schriftart des Texteditors.
     * @param font Die Schriftart, die ausgewählt wurde.
     */
    public insertList(style: "ordered" | "unordered"){
        this._formatEditor.format("list", style)
    }

    /**
     * Ändert die Schriftart des Texteditors.
     * @param font Die Schriftart, die ausgewählt wurde.
     */
    public setAlign(align: "" | "center" | "right"){
        this._formatEditor.format("align", align)
    }
    
    /**
     * Ändert die Schriftart des Texteditors.
     * @param font Die Schriftart, die ausgewählt wurde.
     */
    public toggleBold(){
        if(this.selectedFormat?.bold) this._formatEditor.format("bold", "")
        else {
            this._formatEditor.format("bold", true)
            this.selectedFormat.bold = true
        }
    }
    
    /**
     * Toggelt die Kursiv-Formatierung des ausgewählten Textes.
     * Wenn der Text bereits kursiv formatiert ist, wird die Formatierung entfernt.
     * Wenn der Text nicht kursiv formatiert ist, wird die Formatierung hinzugefügt.
     */
    public toggleItalic(){
        if(this.selectedFormat?.italic) this._formatEditor.format("italic", "")
        else{
            this._formatEditor.format("italic", true)
            this.selectedFormat.italic = true
        }
    }
    
    /**
     * Toggelt die Unterstrich-Formatierung des ausgewählten Textes.
     * Wenn der Text bereits unterstrichen ist, wird die Formatierung entfernt.
     * Wenn der Text nicht unterstrichen ist, wird die Formatierung hinzugefügt.
     */
    public toggleUnderline(){
        if(this.selectedFormat?.underline) this._formatEditor.format("underline", "")
        else {
            this._formatEditor.format("underline", true)
            this.selectedFormat.underline = true
        }
    }
    
    /**
     * Toggelt die Durchgestrichen-Formatierung des ausgewählten Textes.
     * Wenn der Text bereits durchgestrichen ist, wird die Formatierung entfernt.
     * Wenn der Text nicht durchgestrichen ist, wird die Formatierung hinzugefügt.
     */
    public toggleStrikethrough(){
        if(this.selectedFormat?.strike) this._formatEditor.format("strike", "")
        else{
            this._formatEditor.format("strike", true)
            this.selectedFormat.strike = true
        }
    }
    
    /**
     * Toggelt die Hochgestellt-Formatierung des ausgewählten Textes.
     * Wenn der Text bereits hochgestellt ist, wird die Formatierung entfernt.
     * Wenn der Text nicht hochgestellt ist, wird die Formatierung hinzugefügt.
     */
    public async insertLink(){
        setTimeout(async () => {
            const selection: {index: number, length: number} = this._formatEditor.getSelection()
            const url: string = this._formatEditor.getText(selection.index, selection.length);
            if(!url){
                await this.feedback.showError("Bitte markieren Sie erst eine URL im Editor.")
                return
            }
            this._formatEditor.format("link", url)
        }, 100);
    }

    /**
     * Toggelt die Hochgestellt-Formatierung des ausgewählten Textes.
     * Wenn der Text bereits hochgestellt ist, wird die Formatierung entfernt.
     * Wenn der Text nicht hochgestellt ist, wird die Formatierung hinzugefügt.
     */
    public insertCodeBlock(){
        if(this.selectedFormat["code-block"] || false) this._formatEditor.format("code-block", false)
        else this._formatEditor.format("code-block", true)   
    }

    /**
     * Konvertiert den ausgewählten Text im Editor in professionelles Format mithilfe von OpenAI.
     * @returns Eine Promise, die das Ergebnis der Konvertierung enthält.
     */
    public async convertProfessionell(){
        // await this.feedback.updateLoading("Konvertiere Text...")
        let {index, length} = this._formatEditor.getSelection(true)
        const selectedText: string = this._formatEditor.getText(index, length)

        if(!selectedText.length) {
            await this.feedback.hideLoading()
            return await this.feedback.showError("Bitte markieren Sie erst einen Text im Editor.")
        }

        //Sockets definieren und initialisieren
        this._formatEditor.deleteText(index, length)

        const editor = document.querySelectorAll(".ql-editor")[0]
        editor.classList.add("openAiInput")

        if(this.socketSrv.socketState !== signalR.HubConnectionState.Connected) await this.socketSrv.initializeSocket()
        this.socketSrv.registerHandler<string>("OpenAiWriteProfessional", (data: SocketMessageData<string>) => {
            if(!data.complete){
                this._formatEditor.insertText(index, data.data)
                index = index + data.data.length
            }else {
                this.socketSrv.removeHandler<string>("OpenAiWriteProfessional")
                editor.classList.remove("openAiInput")
            }
        })

        const result: HttpFunctionResult<string> = await this.http.POST<{value: string}, string>({
            path: `openai?connectionId=${sessionStorage.getItem("connectionId")}`,
            timeout: 60000,
            body: {value: selectedText},
            statusResult: (status: number, result: HttpFunctionResult<string>) => {
                if(!result.success){
                    this.socketSrv.removeHandler<string>("OpenAiWriteProfessional")
                    editor.classList.remove("openAiInput")
                }
            }
        })
    }
     
}